// In-pane preview for PDF, images, and text — London Smoke & Cure
// PDF/image use Storage SDK getBlob → blob: URL (iframe of the download URL stays blank).

import React, { useEffect, useState } from 'react';
import { getOperatorDriveFileBlob } from '../../services/operatorDriveFileService';
import {
    classifyDrivePreview,
    DRIVE_MEDIA_PREVIEW_MAX_BYTES,
    DRIVE_TEXT_PREVIEW_MAX_BYTES,
} from '../../utils/operatorDrivePreview';
import {
    DRIVE_STORAGE_CORS_MESSAGE,
    fetchDriveStorageUrl,
    formatDriveStorageCorsMessage,
    isLikelyCorsOrNetworkFetchError,
} from '../../utils/operatorDriveShareDownload';

function TextPreview({ url, fileName }) {
    const [text, setText] = useState('');
    const [status, setStatus] = useState('loading');

    useEffect(() => {
        if (!url) {
            setStatus('missing');
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const response = await fetchDriveStorageUrl(url, fileName || 'this file');
                const blob = await response.blob();
                if (blob.size > DRIVE_TEXT_PREVIEW_MAX_BYTES) {
                    if (!cancelled) setStatus('large');
                    return;
                }
                const next = await blob.text();
                if (!cancelled) {
                    setText(next);
                    setStatus('ok');
                }
            } catch (err) {
                if (!cancelled) {
                    setStatus(isLikelyCorsOrNetworkFetchError(err) ? 'cors' : 'error');
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [url, fileName]);

    if (status === 'loading') return <p className="op-drive-preview__note">Loading preview…</p>;
    if (status === 'large') {
        return (
            <p className="op-drive-preview__note">
                This text file is larger than the in-pane preview. Use Open.
            </p>
        );
    }
    if (status === 'cors') {
        return (
            <p className="op-drive-preview__note">
                {formatDriveStorageCorsMessage('default', new Error(DRIVE_STORAGE_CORS_MESSAGE))}
            </p>
        );
    }
    if (status !== 'ok') {
        return (
            <p className="op-drive-preview__note">
                Could not preview {fileName || 'this file'}. Use Open.
            </p>
        );
    }
    return <pre className="op-drive-preview__text">{text}</pre>;
}

/**
 * Load PDF/image bytes (Storage SDK first) and show via blob: URL.
 * @param {{ item: object, kind: 'pdf' | 'image' }} props
 */
function MediaPreview({ item, kind }) {
    const [objectUrl, setObjectUrl] = useState('');
    const [status, setStatus] = useState('loading');
    const [message, setMessage] = useState('');

    useEffect(() => {
        let cancelled = false;
        let createdUrl = '';
        setObjectUrl('');
        setStatus('loading');
        setMessage('');

        (async () => {
            try {
                const sizeHint = Number(item?.sizeBytes) || 0;
                if (sizeHint > DRIVE_MEDIA_PREVIEW_MAX_BYTES) {
                    if (!cancelled) {
                        setStatus('large');
                        setMessage(
                            'This file is larger than the in-pane preview. Use Open or Download.',
                        );
                    }
                    return;
                }

                let blob = null;
                const storagePath = String(item?.storagePath || '').trim();
                if (storagePath) {
                    try {
                        blob = await getOperatorDriveFileBlob(storagePath, item.contentType);
                    } catch (sdkErr) {
                        console.warn('[Smoke Drive] preview getBlob failed:', sdkErr?.code || sdkErr);
                    }
                }

                if (!blob) {
                    const href = String(item?.downloadUrl || '').trim();
                    if (!href) {
                        if (!cancelled) {
                            setStatus('missing');
                            setMessage('Preview is not ready yet.');
                        }
                        return;
                    }
                    const response = await fetchDriveStorageUrl(href, item.name || 'file');
                    blob = await response.blob();
                }

                if (!blob?.size) {
                    if (!cancelled) {
                        setStatus('error');
                        setMessage('That file is empty.');
                    }
                    return;
                }
                if (blob.size > DRIVE_MEDIA_PREVIEW_MAX_BYTES) {
                    if (!cancelled) {
                        setStatus('large');
                        setMessage(
                            'This file is larger than the in-pane preview. Use Open or Download.',
                        );
                    }
                    return;
                }

                const typed =
                    kind === 'pdf'
                        ? new Blob([blob], { type: 'application/pdf' })
                        : blob.type && blob.type !== 'application/octet-stream'
                          ? blob
                          : new Blob([blob], {
                                type: item.contentType || blob.type || 'image/jpeg',
                            });
                createdUrl = URL.createObjectURL(typed);
                if (cancelled) {
                    URL.revokeObjectURL(createdUrl);
                    return;
                }
                setObjectUrl(createdUrl);
                setStatus('ok');
            } catch (err) {
                if (cancelled) return;
                setStatus('error');
                setMessage(
                    formatDriveStorageCorsMessage('default', err) ||
                        `Could not preview ${item?.name || 'this file'}. Use Open.`,
                );
            }
        })();

        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [item?.id, item?.storagePath, item?.downloadUrl, item?.contentType, item?.sizeBytes, item?.name, kind]);

    if (status === 'loading') {
        return <p className="op-drive-preview__note">Loading preview…</p>;
    }
    if (status !== 'ok' || !objectUrl) {
        return <p className="op-drive-preview__note">{message || 'Could not preview this file. Use Open.'}</p>;
    }
    if (kind === 'image') {
        return (
            <div className="op-drive-preview op-drive-preview--image">
                <img src={objectUrl} alt={item.name || 'Preview'} />
            </div>
        );
    }
    return (
        <div className="op-drive-preview op-drive-preview--pdf">
            <iframe
                title={item.name || 'PDF preview'}
                src={`${objectUrl}#toolbar=0`}
            />
        </div>
    );
}

/**
 * @param {{ item: object }} props
 */
export default function OperatorDrivePreview({ item }) {
    const kind = classifyDrivePreview(item);

    if (kind === 'none') {
        return (
            <p className="op-drive-preview__note">
                No in-pane preview for this type. Use Open, Print, or download.
            </p>
        );
    }
    if (kind === 'image' || kind === 'pdf') {
        return <MediaPreview item={item} kind={kind} />;
    }
    const url = item?.downloadUrl || '';
    if (!url && !item?.storagePath) {
        return <p className="op-drive-preview__note">Preview is not ready yet.</p>;
    }
    return (
        <div className="op-drive-preview op-drive-preview--text">
            <TextPreview url={url} fileName={item.name} />
        </div>
    );
}
